import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Info, Users } from "lucide-react";
import { supabase, type Database } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useMessagesPaginated } from "@/hooks/useMessagesPaginated";
import { useAccountMessageSettings } from "@/hooks/useAccountMessageSettings";
import { applyChatContactScopeFilter } from "@/lib/chatContactScope";
import { messageBelongsToContact } from "@/lib/whatsappJid";
import { renderMessageVariables } from "@/lib/messageVariables";
import { applySenderPrefix } from "@/lib/messageSenderPrefix";
import {
  getDisplayName,
  getContactAvatarUrl,
  formatWhatsAppJid,
  isContactUnread,
} from "@/utils/contactUtils";
import { MobileMessageBubble } from "../components/MobileMessageBubble";
import { MobileMessageInput } from "../components/MobileMessageInput";
import { MobileContactInfoSheet } from "../components/MobileContactInfoSheet";

type AccountContact = Database["public"]["Tables"]["account_contacts"]["Row"];

const MobileConversation = () => {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showUserNameInMessages } = useAccountMessageSettings();

  const [contact, setContact] = useState<AccountContact | null>(null);
  const [loadingContact, setLoadingContact] = useState(true);
  const [message, setMessage] = useState("");
  const [infoOpen, setInfoOpen] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  const prevScrollHeightRef = useRef(0);
  const markingReadRef = useRef(false);

  const {
    messages,
    loading: messagesLoading,
    hasMore,
    isLoadingMore,
    loadMoreMessages,
    sendMessage,
    sendImage,
    sendVideo,
    sendDocument,
    sending,
  } = useMessagesPaginated(contact);

  // Buscar o contato pela URL (com filtro de escopo, igual ao chat web).
  // `silent` recarrega sem skeleton nem redirect — usado após editar as infos.
  const loadContact = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!contactId || !user?.account_id || !supabase) return;

      if (!options?.silent) setLoadingContact(true);

      const scoped = applyChatContactScopeFilter(
        supabase
          .from("account_contacts")
          .select("*")
          .eq("account_id", user.account_id)
          .eq("id", contactId),
        user
      );
      const { data, error } = await scoped.maybeSingle();

      if (error || !data) {
        if (!options?.silent) {
          console.warn("[MobileConversation] Contato não encontrado:", contactId, error);
          navigate("/m/chat", { replace: true });
        }
        return;
      }

      setContact(data);
      setLoadingContact(false);
    },
    [contactId, user?.account_id, navigate] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    loadContact();
  }, [loadContact]);

  // Marcar como lido ao abrir e quando novas mensagens chegarem com o chat aberto
  const markAsRead = useCallback(
    async (target: AccountContact) => {
      if (!supabase || !user?.account_id) return;
      if (!isContactUnread(target)) return;
      if (markingReadRef.current) return;

      markingReadRef.current = true;
      const readAt = target.last_message_at ?? new Date().toISOString();
      setContact((prev) => (prev && prev.id === target.id ? { ...prev, last_read_at: readAt } : prev));

      try {
        await supabase
          .from("account_contacts")
          .update({ last_read_at: readAt })
          .eq("id", target.id)
          .eq("account_id", user.account_id);
      } catch (err) {
        console.error("[MobileConversation] Erro ao marcar como lido:", err);
      } finally {
        markingReadRef.current = false;
      }
    },
    [user?.account_id]
  );

  useEffect(() => {
    if (contact) markAsRead(contact);
  }, [contact, markAsRead]);

  // Mensagem nova recebida com a conversa aberta → atualiza last_read_at
  const lastMessage = messages[messages.length - 1];
  useEffect(() => {
    if (!contact || !lastMessage || lastMessage.from_me) return;
    markAsRead({ ...contact, last_message_at: lastMessage.sent_at, last_read_at: contact.last_read_at });
  }, [lastMessage?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredMessages = useMemo(() => {
    if (!contact || messages.length === 0) return messages;
    return messages.filter((msg) => messageBelongsToContact(msg, contact));
  }, [contact, messages]);

  // Controle de posição do scroll
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  // Auto-scroll: para o fim no carregamento inicial e em novas mensagens (se perto do fim)
  useEffect(() => {
    const count = filteredMessages.length;
    const prevCount = prevMessageCountRef.current;
    prevMessageCountRef.current = count;

    if (count === 0) return;

    if (prevCount === 0) {
      // Primeira carga: ir direto para o fim
      requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: "auto" }));
      return;
    }

    if (isLoadingMore) return;

    if (count > prevCount && isNearBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
    }
  }, [filteredMessages.length, isLoadingMore]);

  // Manter posição ao carregar mensagens antigas no topo
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (isLoadingMore) {
      prevScrollHeightRef.current = el.scrollHeight;
    } else if (prevScrollHeightRef.current > 0) {
      const diff = el.scrollHeight - prevScrollHeightRef.current;
      if (diff > 0) el.scrollTop += diff;
      prevScrollHeightRef.current = 0;
    }
  }, [isLoadingMore]);

  // Paginação: carregar mais quando o topo fica visível
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel || !hasMore || messagesLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isLoadingMore) {
          loadMoreMessages();
        }
      },
      { root: scrollRef.current, rootMargin: "100px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, messagesLoading, isLoadingMore, loadMoreMessages]);

  const handleSendMessage = async () => {
    if (!contact || !message.trim()) return;

    const rawText = message.trim();
    const renderedText = renderMessageVariables(rawText, contact);
    const messageToSend = applySenderPrefix(renderedText, user?.name, showUserNameInMessages);
    setMessage("");

    const success = await sendMessage(contact.jid, messageToSend);
    if (!success) {
      setMessage(rawText);
    }
  };

  const handleSendImage = async (imageBase64: string, caption?: string, fileName?: string) => {
    if (!contact) return false;
    return sendImage(contact.jid, imageBase64, caption, fileName);
  };

  const handleSendVideo = async (videoBase64: string, caption?: string, fileName?: string) => {
    if (!contact) return false;
    return sendVideo(contact.jid, videoBase64, caption, fileName);
  };

  const handleSendDocument = async (documentBase64: string, fileName: string, caption?: string) => {
    if (!contact) return false;
    return sendDocument(contact.jid, documentBase64, fileName, caption);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex shrink-0 items-center gap-2 border-b border-border bg-card px-2 pb-2.5 pt-[max(env(safe-area-inset-top),10px)]">
        <button
          type="button"
          onClick={() => navigate("/m/chat")}
          aria-label="Voltar para conversas"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foreground transition-colors active:bg-hover"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>

        {loadingContact || !contact ? (
          <div className="flex flex-1 animate-pulse items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-card-muted" />
            <div className="space-y-1.5">
              <div className="h-3.5 w-32 rounded bg-card-muted" />
              <div className="h-3 w-24 rounded bg-card-muted" />
            </div>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setInfoOpen(true)}
              className="flex min-w-0 flex-1 items-center gap-3 rounded-xl px-1 py-1 text-left transition-colors active:bg-hover"
            >
              <img
                src={getContactAvatarUrl(contact)}
                alt=""
                className="h-10 w-10 shrink-0 rounded-full object-cover"
              />
              <div className="min-w-0">
                <p className="truncate text-[15px] font-semibold text-foreground">
                  {getDisplayName(contact)}
                </p>
                <p className="flex items-center gap-1 truncate text-[12px] text-muted-foreground">
                  {contact.is_group ? (
                    <>
                      <Users className="h-3 w-3" /> Grupo
                    </>
                  ) : (
                    contact.phone || formatWhatsAppJid(contact.jid)
                  )}
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setInfoOpen(true)}
              aria-label="Informações do contato"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors active:bg-hover"
            >
              <Info className="h-[22px] w-[22px]" />
            </button>
          </>
        )}
      </header>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="chat-wallpaper min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3"
      >
        {hasMore && filteredMessages.length > 0 && <div ref={topSentinelRef} className="h-1" />}

        {isLoadingMore && (
          <div className="flex items-center justify-center py-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
          </div>
        )}

        {!hasMore && !messagesLoading && filteredMessages.length > 0 && (
          <p className="py-2 text-center text-[11px] text-muted-foreground">Início da conversa</p>
        )}

        {messagesLoading ? (
          <div className="space-y-3 pt-2">
            {[70, 45, 60, 40].map((w, i) => (
              <div key={i} className={`flex ${i % 2 ? "justify-end" : "justify-start"}`}>
                <div
                  className="h-12 animate-pulse rounded-2xl bg-card-muted"
                  style={{ width: `${w}%` }}
                />
              </div>
            ))}
          </div>
        ) : filteredMessages.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="rounded-full bg-card px-4 py-2 text-[13px] text-muted-foreground shadow-sm">
              Nenhuma mensagem ainda. Diga olá!
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredMessages.map((msg) => (
              <MobileMessageBubble key={msg.id} message={msg} />
            ))}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <MobileMessageInput
        message={message}
        setMessage={setMessage}
        onSendMessage={handleSendMessage}
        onSendImage={handleSendImage}
        onSendVideo={handleSendVideo}
        onSendDocument={handleSendDocument}
        sending={sending}
      />

      <MobileContactInfoSheet
        open={infoOpen}
        onOpenChange={setInfoOpen}
        contact={contact}
        onContactUpdated={() => loadContact({ silent: true })}
      />
    </div>
  );
};

export default MobileConversation;
